import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UnitGenerationRunStatus } from '@duke-hq/svc-api-db/client';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

type ListGenerationsItem = {
  id: string;
  status: UnitGenerationRunStatus;
  progress: number;
  message: string | null;
  error: string | null;
};

type ListGenerationsData = {
  items: ListGenerationsItem[];
  pagination: unknown;
};

const RESUBSCRIBE_DELAY_MS = 5_000;

const TERMINAL_STATUSES: ReadonlySet<UnitGenerationRunStatus> = new Set([
  UnitGenerationRunStatus.IMPORTED,
  UnitGenerationRunStatus.FAILED,
  UnitGenerationRunStatus.CANCELLED,
]);

export function useGenerationProgressSubscription(
  runId: string,
  status: UnitGenerationRunStatus | undefined,
  error: string | null | undefined,
) {
  const utils = trpcAdmin.useUtils();
  const queryClient = useQueryClient();

  const isAwaitingIngestion = status === UnitGenerationRunStatus.GENERATED && error == null;

  const { status: subscriptionStatus, reset: resubscribe } =
    trpcAdmin.unitsOfCompetency.subscribeProgress.useSubscription(
      { runId },
      {
        enabled: status === UnitGenerationRunStatus.RUNNING || isAwaitingIngestion,
        onData: (data) => {
          utils.unitsOfCompetency.generationById.setData({ id: runId }, (prev) => {
            if (prev == null) {
              return prev;
            }

            return {
              ...prev,
              status: data.status,
              progress: data.progress,
              message: data.message,
              error: data.error,
            };
          });

          queryClient.setQueriesData<ListGenerationsData>(
            { queryKey: [['unitsOfCompetency', 'listGenerations']] },
            (prev) => {
              if (prev == null) {
                return prev;
              }

              return {
                ...prev,
                items: prev.items.map((item) =>
                  item.id === runId
                    ? {
                        ...item,
                        status: data.status,
                        progress: data.progress,
                        message: data.message,
                        error: data.error,
                      }
                    : item,
                ),
              };
            },
          );

          const isTerminal =
            TERMINAL_STATUSES.has(data.status) ||
            (data.status === UnitGenerationRunStatus.GENERATED && data.error != null);

          if (isTerminal || data.status === UnitGenerationRunStatus.WAITING) {
            void utils.unitsOfCompetency.listGenerations.invalidate();
            void utils.unitsOfCompetency.generationById.invalidate({ id: runId });
          }
        },
      },
    );

  useEffect(() => {
    if (subscriptionStatus !== 'error') {
      return;
    }

    const timer = setTimeout(() => {
      void utils.unitsOfCompetency.generationById.invalidate({ id: runId });
      void utils.unitsOfCompetency.listGenerations.invalidate();
      resubscribe();
    }, RESUBSCRIBE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [subscriptionStatus, resubscribe, runId, utils]);
}
